import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  WorkspacePremium as PremiumIcon,
  Check as CheckIcon,
  Add as AddIcon,
  East as EastIcon,
  Close as CloseIcon,
  Lock as LockIcon,
} from '@mui/icons-material';

interface Plan {
  id: string;
  name: string;
  price: number;
  period: string;
  description: string;
  features: string[];
  maxSchedules: number;
  maxStaff: number;
  planId: string | null;
  badge?: string;
}

export const DEFAULT_PLANS: Plan[] = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    period: 'month',
    description: 'Free plan for individuals getting started and staff members',
    features: ['1 schedule', 'Up to 5 staff members', 'Basic features'],
    maxSchedules: 1,
    maxStaff: 5,
    planId: null,
  },
  {
    id: 'individual',
    name: 'Individual',
    price: 5,
    period: 'month',
    description: 'Ideal for individuals getting started',
    features: ['1 schedule', 'Up to 5 staff members', 'Basic features'],
    maxSchedules: 1,
    maxStaff: 5,
    planId: '???',//TODO: get actual plan ID from Stripe
  },
  {
    id: 'basic',
    name: 'Basic',
    price: 10,
    period: 'month',
    description: 'Ideal for small teams',
    features: ['1 schedule', 'Up to 5 staff members', 'Basic features', 'Business support', 'Analytics'],
    maxSchedules: 1,
    maxStaff: 5,
    planId: 'v79p',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 30,
    period: 'month',
    description: 'Ideal for growing businesses',
    features: ['10 schedules', 'Up to 15 staff members', 'Schedule templates', 'Basic analytics', 'Business support', 'Analytics'],
    maxSchedules: 10,
    maxStaff: 15,
    planId: '2fwt',
    badge: 'Most Popular',
  },
  {
    id: 'business',
    name: 'Business',
    price: 80,
    period: 'month',
    description: 'Ideal for established businesses',
    features: [
      'Unlimited schedules',
      'Up to 35 staff members',
      'Schedule templates',
      'Advanced analytics',
      'Priority support',
      'Business support',
      'Analytics',
    ],
    maxSchedules: -1,
    maxStaff: 35,
    planId: 'cdpj',
  },
];

interface UpgradeAccountModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  featureName?: string;
  currentPlan?: string;
  plans?: Plan[];
}

export default function UpgradeAccountModal({
  open,
  onClose,
  onUpgrade,
  featureName = 'this feature',
  currentPlan = 'Free',
  plans = DEFAULT_PLANS,
}: UpgradeAccountModalProps) {
  const currentPlanObj = plans.find(
    (p) => p.name.toLowerCase() === currentPlan.toLowerCase()
  ) ?? plans[0];

  const currentIndex = plans.indexOf(currentPlanObj);
  const nextPlanObj: Plan | undefined = plans[currentIndex + 1];
  const isAlreadyMax = !nextPlanObj;

  // Features the user already has on their current plan
  const currentFeatureSet = new Set(currentPlanObj.features);

  // New features gained by upgrading
  const newFeatures = nextPlanObj
    ? nextPlanObj.features.filter((f) => !currentFeatureSet.has(f))
    : [];
  const keptFeatures = nextPlanObj
    ? nextPlanObj.features.filter((f) => currentFeatureSet.has(f))
    : [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)',
        },
      }}
    >
      {/* Hero header */}
      <Box
        sx={{
          background: 'linear-gradient(335deg, #1b71b1 0%, #0B74D1 60%, #338eda 100%)',
          pt: 4,
          pb: 3.5,
          px: 3,
          position: 'relative',
          textAlign: 'center',
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: 'rgba(255,255,255,0.7)',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <PremiumIcon sx={{ fontSize: 32, color: '#fbbf24' }} />
        </Box>

        <Typography variant="h5" fontWeight={700} color="#fff" gutterBottom>
          {isAlreadyMax ? 'You\'re on the top plan!' : `Upgrade to ${nextPlanObj.name}`}
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 1,
              px: 0.75,
              py: 0.25,
              mr: 0.5,
            }}
          >
            <LockIcon sx={{ fontSize: '0.75rem' }} />
            {featureName}
          </Box>
          requires a higher plan
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Chip
            label={`Currently on ${currentPlanObj.name}`}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,0.12)',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.7rem',
              height: 22,
              fontWeight: 500,
            }}
          />
          {nextPlanObj && (
            <Chip
              label={`${nextPlanObj.name} — $${nextPlanObj.price}/${nextPlanObj.period}`}
              size="small"
              sx={{
                bgcolor: '#fbbf24',
                color: '#78350f',
                fontSize: '0.7rem',
                height: 22,
                fontWeight: 700,
              }}
            />
          )}
        </Box>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        {isAlreadyMax ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 2 }}>
            You already have access to all features.
          </Typography>
        ) : (
          <>
            {newFeatures.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  sx={{ color: 'primary.main', letterSpacing: 1.2, fontWeight: 700, fontSize: '0.65rem' }}
                >
                  New with {nextPlanObj.name}
                </Typography>
                <List disablePadding sx={{ mt: 0.5, mb: 1 }}>
                  {newFeatures.map((feature, idx) => (
                    <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <AddIcon sx={{ fontSize: '0.7rem', color: '#fff' }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}

            {keptFeatures.length > 0 && (
              <>
                {newFeatures.length > 0 && <Divider sx={{ my: 1.5 }} />}
                <Typography
                  variant="overline"
                  sx={{ color: 'text.disabled', letterSpacing: 1.2, fontWeight: 600, fontSize: '0.65rem' }}
                >
                  What's Included in {nextPlanObj.name}
                </Typography>
                <List disablePadding sx={{ mt: 0.5 }}>
                  {keptFeatures.map((feature, idx) => (
                    <ListItem key={idx} disableGutters sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Box
                          sx={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            bgcolor: 'success.main',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <CheckIcon sx={{ fontSize: '0.7rem', color: '#fff' }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary={feature}
                        primaryTypographyProps={{ variant: 'body2', fontWeight: 400, color: 'text.secondary' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 3, pt: 2 }}>
        <Button
          onClick={onUpgrade}
          fullWidth
          variant="contained"
          endIcon={<EastIcon />}
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.95rem',
            py: 1.25,
            borderRadius: 2,
          }}
        >
          View Plans &amp; Upgrade
        </Button>
        <Button
          onClick={onClose}
          fullWidth
          variant="text"
          sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
        >
          Maybe later
        </Button>
      </DialogActions>
    </Dialog>
  );
}